import { useEffect, useState } from "react";
import { useViewAsClient } from "@/lib/viewAsClient";
import { toast } from "sonner";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CalendarClock,
  Pencil,
  Plus,
  Repeat,
  Trash2,
  Wand2,
} from "lucide-react";
import {
  deleteRecurringPattern,
  generateRecurringNow,
  listRecurringPatterns,
  listSitesLite,
  togglePatternActive,
} from "@/lib/api";
import { DAYS_OF_WEEK } from "@/lib/constants";
import RecurringPatternForm from "@/components/RecurringPatternForm";

export default function RecurringPatterns() {
  const { viewAsClientId } = useViewAsClient();
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // row OR { __new: true }
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [tick, setTick] = useState(0);
  const refresh = () => setTick((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await listSitesLite();
      if (cancelled) return;
      setSites(data || []);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await listRecurringPatterns({
        siteId: siteFilter === "all" ? null : siteFilter,
        clientId: viewAsClientId || null,
      });
      if (cancelled) return;
      if (error) toast.error(error.message);
      setRows(data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [siteFilter, viewAsClientId, tick]);

  const toggleActive = async (row) => {
    const next = !row.is_active;
    const { error } = await togglePatternActive(row.id, next);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(next ? "Pattern resumed." : "Pattern held.");
    refresh();
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    const { error } = await deleteRecurringPattern(confirmDelete.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Pattern deleted.");
    setConfirmDelete(null);
    refresh();
  };

  const [generating, setGenerating] = useState(false);
  const onGenerate = async () => {
    setGenerating(true);
    const { data, error } = await generateRecurringNow({ horizonDays: 14 });
    setGenerating(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    const n = data?.created ?? 0;
    toast.success(
      n > 0
        ? `${n} booking${n === 1 ? "" : "s"} generated for the next 14 days.`
        : "Up to date — no new bookings needed."
    );
  };

  return (
    <div data-testid="recurring-patterns-page" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Repeat className="h-6 w-6 text-slate-700" /> Recurring bookings
          </h1>
          <p className="text-sm text-slate-500">
            Patterns auto-generate bookings on a schedule. Toggle the switch off to <strong>hold</strong> a
            pattern — held patterns show as ghosted previews on the Dock manager grid.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={onGenerate}
            disabled={generating}
            data-testid="pattern-generate-now"
            title="Materialise real bookings for active patterns over the next 14 days"
          >
            <Wand2 className="h-4 w-4 mr-1.5" />
            {generating ? "Generating…" : "Generate next 14 days"}
          </Button>
          <Button onClick={() => setEditing({ __new: true })} data-testid="pattern-new-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New pattern
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="py-3 flex flex-wrap items-center gap-3">
          <span className="text-xs text-slate-500">Site</span>
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger className="w-60" data-testid="pattern-site-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sites</SelectItem>
              {sites.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name} {s.code ? `· ${s.code}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-xs text-slate-500 ml-auto">
            {loading ? "Loading…" : `${rows.length} pattern${rows.length === 1 ? "" : "s"}`}
          </div>
        </CardContent>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Site / bay</TableHead>
              <TableHead>Pattern</TableHead>
              <TableHead>Window</TableHead>
              <TableHead>Dates</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-sm text-slate-400">
                  No recurring patterns yet.
                  <Button
                    variant="link"
                    onClick={() => setEditing({ __new: true })}
                    data-testid="pattern-empty-cta"
                  >
                    Create the first one
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} data-testid={`pattern-row-${r.id}`}>
                  <TableCell>
                    <div className="font-medium">
                      {r.site?.name || "—"}
                      {r.site?.code && <span className="text-slate-400 ml-1">· {r.site.code}</span>}
                    </div>
                    <div className="text-xs flex items-center gap-1.5 text-slate-500 mt-0.5">
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ backgroundColor: r.bay?.color || "#94a3b8" }}
                      />
                      {r.bay?.name || "—"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="capitalize text-sm">{r.pattern_type}</div>
                    <div className="text-xs text-slate-500">{daysLabel(r)}</div>
                  </TableCell>
                  <TableCell className="tabular-nums text-sm">
                    {(r.time_start || "").slice(0, 5)} – {(r.time_end || "").slice(0, 5)}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {r.start_date} → {r.end_date || "open-ended"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={r.is_active}
                        onCheckedChange={() => toggleActive(r)}
                        data-testid={`pattern-toggle-${r.id}`}
                      />
                      <Badge
                        variant="secondary"
                        className={
                          r.is_active
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-100 text-slate-500"
                        }
                      >
                        {r.is_active ? "Active" : "Held"}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="inline-flex items-center gap-0.5">
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit pattern"
                              onClick={() => setEditing(r)} data-testid={`pattern-edit-${r.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600 hover:bg-rose-50" title="Delete pattern"
                              onClick={() => setConfirmDelete(r)} data-testid={`pattern-delete-${r.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <RecurringPatternForm
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        pattern={editing?.__new ? null : editing}
        defaultSiteId={editing?.__new && siteFilter !== "all" ? siteFilter : null}
        onSaved={refresh}
      />

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete recurring pattern?</AlertDialogTitle>
            <AlertDialogDescription>
              This stops new bookings from being generated. Existing generated bookings are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="pattern-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function daysLabel(p) {
  if (p.pattern_type === "daily") return "Every day";
  if (p.pattern_type === "monthly") return `Day ${new Date(`${p.start_date}T00:00:00`).getDate()} each month`;
  const days = (p.day_of_week || [])
    .map((d) => DAYS_OF_WEEK.find((x) => x.value === d)?.label)
    .filter(Boolean)
    .join(" · ");
  return days || "—";
}
